import { useCallback, useContext, useEffect, useRef, useState } from "react"

//Helpers
import { LogError } from "../../../helpers/logger"

//PrimeReact
import { Button } from "primereact/button"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { InputSwitch } from "primereact/inputswitch"
import { ConfirmDialog } from "primereact/confirmdialog" // For <ConfirmDialog /> component
import { confirmDialog } from "primereact/confirmdialog" // For confirmDialog method

//Styles
import myStyles from "../../../styles/assessments/AssessmentBlock.module.css"
import assessStyles from "../../../styles/assessments/Assessment.module.css"

// Context
import { UserContext } from "../../../context/userContext"

//Assessment modules
import {
  assessmentsApiCheckStatus,
  assessmentsApiListMiniPost,
} from "../assessmentsApi"
import AddAssessment from "./AddAssessment"
import AssessmentForm from "../AssessmentForm/AssessmentForm"
import AssessmentBlockSession from "./AssessmentBlockSession"
import { LoaderSmall } from "../../../components/Loaders"
import AssessmentBlockTask from "./AssessmentBlockTask"
import { Panel } from "primereact/panel"

export default function AssessmentBlock({
  assessmentConfig,
  sessionTemplateId,
  assessmentTaskProfile,
  onSubmitAssessments,
  onCompleteTask,
  onSetParentLoading,
  parentIsLoading,
  onUpdateSessionValues,
  readOnly,
  adminMode, //hides Status column, show a switchButton for Required & hides complete button
  sessionMode, //Submits completed assessments with SOAR ad_hoc_assessment session.
  hideTitle,
  stageCompletedAssessmentUpdates,
  actionButtons,
}) {
  const userCtx = useContext(UserContext)

  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingAssessments, setIsLoadingAssessments] = useState(true)
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(true)
  const [sessionLocation, setSessionLocation] = useState(undefined)
  const [sessionPrograms, setSessionPrograms] = useState(undefined)
  const [sessionDate, setSessionDate] = useState(new Date())
  const [availableAssessments, setAvailableAssessments] = useState(undefined)
  const [showAddAssessment, setShowAddAssessment] = useState(false)
  const [showAssessment, setShowAssessment] = useState(undefined)
  const [assessmentStatus, setAssessmentStatus] = useState(undefined)

  const howToUsePanelRef = useRef()

  const checkAssessmentStatus = useCallback(async () => {
    try {
      if (
        assessmentConfig &&
        assessmentConfig.length > 0 &&
        isLoadingStatuses
      ) {
        const statusResult = await assessmentsApiCheckStatus(
          userCtx.tokenAssessments,
          {
            enterpriseId: userCtx.session.user.enterprise_id,
            organizationId: userCtx.session.user.organization_id,
            assessmentIds: assessmentConfig.map((assessment) => {
              return assessment.assessment_id
            }),
          },
        )
        setAssessmentStatus(statusResult)
        setIsLoadingStatuses(false)

        if (!adminMode) {
          const unavailableAssessments = statusResult.assessments.filter(
            (assessment) => assessment.isUnavailable || assessment.isDisabled,
          )

          if (unavailableAssessments.length > 0) {
            const newAssessmentConfig = assessmentConfig.map((config) => {
              if (
                unavailableAssessments.some(
                  (assessment) =>
                    assessment.assessmentId === config.assessment_id,
                ) &&
                config.required
              ) {
                return { ...config, required: false }
              } else {
                return config
              }
            })

            onSubmitAssessments(newAssessmentConfig)
          }
        }
      }
    } catch (error) {
      LogError("Unable to confirm assessment statuses", error)
      userCtx.setNotificationError("Unable to confirm assessment statuses")

      const errorStatus = assessmentConfig.map((assessment) => {
        return {
          assessmentId: assessment.assessment_id,
          isUnavailable: true,
        }
      })
      setAssessmentStatus({
        isActiveOrganization: false,
        assessments: errorStatus,
      })
      setIsLoadingStatuses(false)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentConfig])

  useEffect(() => {
    checkAssessmentStatus()
  }, [checkAssessmentStatus])

  const loadAvailableAssessments = useCallback(async () => {
    try {
      setIsLoading(true)
      setIsLoadingAssessments(true)
      const assessmentListing = await assessmentsApiListMiniPost(
        userCtx.tokenAssessments,
        {
          organizationId: userCtx.session.user.organization_id,
          enterpriseId: userCtx.session.user.enterprise_id,
          includeDisabledAssessments: false,
          includeDisabledSystemAssessments: false,
        },
      )

      setAvailableAssessments(assessmentListing?.assessments)
      setIsLoading(false)
      setIsLoadingAssessments(false)
    } catch (error) {
      LogError("Unable to load the available list of assessments", error)
      userCtx.setNotificationError("Unable to load available assessment list")
      setAvailableAssessments(undefined)
      setIsLoading(false)
      setIsLoadingAssessments(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.session.user.organization_id, userCtx.session.user.enterprise_id])

  useEffect(() => {
    loadAvailableAssessments()
  }, [loadAvailableAssessments])

  const ActionBodyTemplate = (rowData) => {
    let iconName = rowData.completed_assessment_id ? "edit" : "task"
    let tooltip = rowData.completed_assessment_id
      ? "Edit values"
      : "Complete assessment"
    let disabled =
      isLoading ||
      readOnly ||
      assessmentTaskProfile?.taskIsDue === false ||
      parentIsLoading

    if (assessmentTaskProfile?.dateCompleted) {
      iconName = "visibility"
      tooltip = "View values"
      if (!rowData.completed_assessment_id) {
        disabled = true
      }
    }

    let status = assessmentStatus?.assessments?.find(
      (assessment) => assessment.assessmentId === rowData.assessment_id,
    )

    return (
      <div className={assessStyles.actionButtonContainer}>
        {!adminMode ? (
          <Button
            type="button"
            onClick={() => {
              setShowAssessment(rowData)
            }}
            className={assessStyles.actionButtonPurple}
            disabled={disabled || status?.isUnavailable}
            tooltip={tooltip}
          >
            <span className={`material-icons`}>{iconName}</span>
          </Button>
        ) : null}
        {!readOnly && !assessmentTaskProfile ? (
          <Button
            type="button"
            onClick={() => {
              const newAssessmentList = [
                ...assessmentConfig.filter(
                  (assessment) => assessment !== rowData,
                ),
              ]

              //Cleanup assessment statuses.
              if (assessmentStatus?.assessments) {
                let newAssessmentStatus = assessmentStatus
                newAssessmentStatus.assessments =
                  newAssessmentStatus.assessments.filter(
                    (assessment) =>
                      assessment.assessmentId !== rowData.assessment_id,
                  )

                setAssessmentStatus(newAssessmentStatus)
              }

              onSubmitAssessments(newAssessmentList)
            }}
            className={assessStyles.actionButtonPurple}
            disabled={
              isLoading ||
              readOnly ||
              (!adminMode && !rowData.session_assessment) || //Only allow deletions of assessments added during the block session
              (rowData.completed_assessment_id && sessionMode) //Disable delete if this is a completed assessment and a soar session is created on submission
            }
            tooltip="Delete Assessment"
          >
            <span className={`material-icons`}>{"delete"}</span>
          </Button>
        ) : null}
      </div>
    )
  }

  const StatusBodyTemplate = (rowData) => {
    let pendingLabel = "Pending"

    if (
      assessmentTaskProfile?.dateCompleted &&
      !rowData.completed_assessment_id
    ) {
      pendingLabel = "Declined"
    }

    if (rowData.completed_assessment_id) {
      return (
        <div className={`${assessStyles.badge} ${assessStyles.badgeCompleted}`}>
          Completed
        </div>
      )
    } else if (rowData.required) {
      return (
        <div
          className={`${assessStyles.badge} ${assessStyles.badgePendingRequired}`}
        >
          {pendingLabel}
        </div>
      )
    } else {
      return (
        <div className={`${assessStyles.badge} ${assessStyles.badgePending}`}>
          {pendingLabel}
        </div>
      )
    }
  }

  const RequiredBodyTemplate = (rowData) => {
    if (adminMode) {
      let status = assessmentStatus?.assessments?.find(
        (assessment) => assessment.assessmentId === rowData.assessment_id,
      )

      return (
        <div
          className={`${myStyles.columnRequired} ${assessStyles.switchControl}`}
        >
          <InputSwitch
            checked={rowData.required}
            onChange={(e) => {
              const newConfig = [...assessmentConfig]
              const index = newConfig.indexOf(rowData)
              newConfig[index].required = e.value

              onSubmitAssessments(newConfig)
            }}
            disabled={status?.isUnavailable}
          />
        </div>
      )
    } else {
      return (
        <div className={myStyles.columnRequired}>
          <span className={`material-icons`}>
            {rowData.required ? "check" : "remove"}
          </span>
        </div>
      )
    }
  }

  const AssessmentNameTemplate = (rowData) => {
    let name = rowData.assessment_name
    let status = assessmentStatus?.assessments?.find(
      (assessment) => assessment.assessmentId === rowData.assessment_id,
    )

    if (status?.assessmentName) {
      name = status.assessmentName
    }

    if (!name && !isLoading) {
      const loadedAssessment = availableAssessments.find(
        (assessment) => assessment.id === rowData.assessment_id,
      )

      if (loadedAssessment) {
        name = loadedAssessment.name
      }
    }

    if (!name && isLoading) {
      return <LoaderSmall />
    } else {
      return (
        <div className={myStyles.assessmentName}>
          {status?.isUnavailable || status?.isDisabled ? (
            <div
              className={
                status?.isDisabled
                  ? myStyles.assessmentErrorIcon
                  : myStyles.assessmentWarningIcon
              }
            >
              <span className={`material-icons`}>{"warning"}</span>
            </div>
          ) : null}
          {name ? name : "Unknown assessment"}
        </div>
      )
    }
  }

  const markTaskAsComplete = async () => {
    try {
      setIsLoading(true)
      onSetParentLoading(true)
      onCompleteTask()
      setIsLoading(false)
      onSetParentLoading(false)
    } catch (error) {
      LogError("Unable to complete task", error)
      userCtx.setNotificationError(
        "Unable to mark assessment task as completed",
      )
      setIsLoading(false)
      onSetParentLoading(false)
    }
  }

  const getAssessmentList = () => {
    let list = assessmentConfig

    if (
      !adminMode &&
      assessmentStatus?.assessments?.some((assessment) => assessment.isDisabled)
    ) {
      list = list.filter(
        (config) =>
          !assessmentStatus.assessments.some(
            (assessment) =>
              assessment.assessmentId === config.assessment_id &&
              assessment.isDisabled,
          ),
      )
    }

    return list
  }

  const AssessmentsPlusSubscriptionWarning = () => {
    const containsUnavailableAssessments = assessmentStatus?.assessments?.some(
      (assessment) => assessment.isUnavailable,
    )

    if (
      containsUnavailableAssessments &&
      !assessmentStatus.isActiveOrganization
    ) {
      return (
        <div className={myStyles.assessmentWarning}>
          <div className={myStyles.assessmentWarningIcon}>
            <span className={`material-icons`}>{"warning"}</span>
          </div>
          This block configuration contains customs assessments that are locked.
          To unlock these, an Assessments+ subscription is required.
        </div>
      )
    } else {
      return null
    }
  }

  const DisabledAssessmentsWarning = () => {
    if (
      assessmentStatus?.assessments?.some((assessment) => assessment.isDisabled)
    ) {
      return (
        <div className={myStyles.assessmentError}>
          <div className={myStyles.assessmentErrorIcon}>
            <span className={`material-icons`}>{"warning"}</span>
          </div>
          This block configuration contains assessments that are disabled.
        </div>
      )
    } else {
      return null
    }
  }

  const ScheduledTaskHelperText = () => {
    if (assessmentTaskProfile) {
      return (
        <Panel
          ref={howToUsePanelRef}
          header={"How to use this interface "}
          className={myStyles.assessmentInformationPanel}
          toggleable
          collapsed
        >
          <div className={myStyles.assessmentInformation}>
            <p>
              The assessments listed below have been scheduled for completion.
            </p>
            <p>
              Once done, clicking on "Complete scheduled task" will close this
              scheduled assessments task and mark any unprocessed, optional
              assessments as declined. You will not be able to re-open this
              instance again.{" "}
            </p>
            <p>
              If you would like to return later to complete the assessments, you
              can click on the "Close" button.
            </p>
          </div>
        </Panel>
      )
    } else {
      return null
    }
  }

  return (
    <>
      <ConfirmDialog />
      {showAddAssessment ? (
        <AddAssessment
          availableAssessments={availableAssessments.filter(
            (assessment) =>
              !assessmentConfig?.some(
                (current) => current.assessment_id === assessment.id,
              ),
          )}
          onClose={() => {
            setShowAddAssessment(false)
          }}
          onAdd={(newAssessments) => {
            let newAssessmentList = []

            if (assessmentConfig) {
              newAssessmentList = assessmentConfig
            }

            newAssessmentList = [...newAssessmentList, ...newAssessments]
            onSubmitAssessments(newAssessmentList)

            setShowAddAssessment(false)
          }}
        />
      ) : null}
      {showAssessment ? (
        <AssessmentForm
          assessmentId={showAssessment.assessment_id}
          completedAssessmentId={showAssessment.completed_assessment_id}
          stagedAssessmentId={showAssessment.staged_assessment_id}
          stageCompletedAssessmentUpdates={stageCompletedAssessmentUpdates}
          taskId={assessmentTaskProfile?.id}
          readOnly={assessmentTaskProfile?.dateCompleted}
          sessionTemplateId={sessionTemplateId}
          sessionLocation={sessionLocation}
          showForm={true}
          onCancel={() => {
            setShowAssessment(undefined)
          }}
          onSubmit={async ({
            completedAssessmentId,
            stagedAssessmentId,
            score,
          }) => {
            const index = assessmentConfig.indexOf(showAssessment)
            let newAssessments = [...assessmentConfig]
            newAssessments[index] = {
              ...assessmentConfig[index],
              completed_assessment_id: completedAssessmentId,
              staged_assessment_id: stagedAssessmentId,
              score,
            }

            onSubmitAssessments(newAssessments)

            setShowAssessment(undefined)
            //If this is a task and all assessments have been completed, mark the assessment as completed.
            if (
              assessmentTaskProfile &&
              !assessmentTaskProfile.dateCompleted &&
              !newAssessments.some(
                (assessment) => !assessment.completed_assessment_id,
              )
            ) {
              confirmDialog({
                message: (
                  <>
                    <p>
                      All assessments completed, do you want to mark the task as
                      done?
                    </p>
                    <br />
                    <p>
                      If not, please manually click on "Complete scheduled task"
                      when ready
                    </p>
                  </>
                ),
                header: "Confirmation",
                icon: "pi pi-exclamation-triangle",
                defaultFocus: "accept",
                accept: async () => {
                  await markTaskAsComplete()
                },
              })
            }
          }}
        />
      ) : null}
      <div
        className={
          sessionMode ? myStyles.mainLayoutWithSession : myStyles.mainLayout
        }
      >
        {!hideTitle ? (
          <div className={myStyles.mainLayoutHeader}>
            <div className={myStyles.title}>Assessments</div>
            {actionButtons}
          </div>
        ) : null}

        {!assessmentTaskProfile && !readOnly ? (
          <div className={myStyles.mainLayoutAction}>
            <Button
              label="Add assessment"
              className={`${assessStyles.button} ${myStyles.mainActionButton} ${myStyles.button}`}
              type="button"
              disabled={
                !availableAssessments || readOnly || isLoadingAssessments
              }
              loading={isLoadingAssessments}
              onClick={() => {
                setShowAddAssessment(true)
              }}
            />
          </div>
        ) : null}

        {sessionMode ? (
          <>
            <div className={myStyles.mainLayoutTask}>
              <AssessmentBlockTask
                taskProfile={assessmentTaskProfile}
                taskCompletedDate={assessmentTaskProfile?.dateCompleted}
              />
            </div>

            <div className={myStyles.mainLayoutSession}>
              {!assessmentTaskProfile ||
              !assessmentTaskProfile?.dateCompleted ? (
                <AssessmentBlockSession
                  locationId={sessionLocation}
                  onLocationIdChange={(newLocation) => {
                    setSessionLocation(newLocation)
                    if (onUpdateSessionValues) {
                      onUpdateSessionValues({
                        location: newLocation,
                        sessionDate: sessionDate,
                        programs: sessionPrograms,
                      })
                    }
                  }}
                  sessionDate={sessionDate}
                  onSessionDateChange={(newSessionDate) => {
                    setSessionDate(newSessionDate)
                    if (onUpdateSessionValues) {
                      onUpdateSessionValues({
                        location: sessionLocation,
                        sessionDate: newSessionDate,
                        programs: sessionPrograms,
                      })
                    }
                  }}
                  activePrograms={sessionPrograms}
                  onChangeActivePrograms={(newPrograms) => {
                    setSessionPrograms(newPrograms)
                    if (onUpdateSessionValues) {
                      onUpdateSessionValues({
                        location: sessionLocation,
                        sessionDate: sessionDate,
                        programs: newPrograms,
                      })
                    }
                  }}
                  readOnly={readOnly}
                />
              ) : null}
            </div>
          </>
        ) : null}

        <div className={myStyles.mainLayoutBody}>
          <AssessmentsPlusSubscriptionWarning />
          <DisabledAssessmentsWarning />
          <ScheduledTaskHelperText />

          <DataTable
            className={assessStyles.dataTable}
            value={getAssessmentList()}
          >
            <Column header={"Assessment name"} body={AssessmentNameTemplate} />
            {!adminMode ? (
              <Column
                header={"Status"}
                body={StatusBodyTemplate}
                className={myStyles.statusColumn}
              />
            ) : null}

            <Column
              header={"Required"}
              body={RequiredBodyTemplate}
              className={myStyles.requiredColumn}
            />
            <Column
              body={ActionBodyTemplate}
              className={myStyles.actionColumn}
            />
          </DataTable>
        </div>
      </div>
    </>
  )
}
